/* eslint-disable */
import axios from 'axios'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react'
import { useNavigate } from 'react-router-dom'

import { useLoading } from './LoadingContext'
import { toast } from './NotificationContext'

import { useAuthStore } from '@/store/authStore'
import { useStorage } from '@/hooks/useStorage'
import type { AuthResponse, UserBasic } from '@/types/auth'
import { ApiRoutes } from '@/services/api/apiRoutes'

interface AuthContextType {
    user: UserBasic | null
    idAspirant: number | null
    accessToken: string | null
    refreshToken: string | null
    login: (
        loginInformation: AuthResponse,
        isAgentAccess?: boolean
    ) => Promise<void>
    logout: () => void
    isAuthenticated: boolean
    isAgent: boolean
    idAsesor: number | null
    firstName: string | null
    lastName: string | null
    email: string | null
    cellphone: string | null
    studyModality: string | null
    getAccessToken: () => string | null
    getRefreshToken: () => string | null
    getNewAccessToken: (
        oldAccessToken: string,
        refreshToken: string
    ) => Promise<string | null>
    idInscription: number | null
    programName: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({
    children
}: React.PropsWithChildren): JSX.Element {
    const navigate = useNavigate()
    const { setIsLoading } = useLoading()

    // Track initialization state to prevent usage before ready
    const [isInitialized, setIsInitialized] = useState(false)

    const {
        setAuth,
        clearAuth,
        updateTokens,
        user,
        idAspirant,
        accessToken,
        refreshToken,
        isAuthenticated,
        idInscription,
        programName,
        firstName,
        lastName,
        email,
        cellphone,
        studyModality,
        isAgent,
        idAsesor
    } = useAuthStore()

    // Use our custom storage hook for better agent/aspirant storage management
    const { clearAuthStorage, setItem } = useStorage()

    const login = useCallback(
        async (loginInformation: AuthResponse, isAgentAccess = false) => {
            try {
                setIsLoading(true)
                console.log('loginInformation', loginInformation)

                // If agent is accessing an aspirant account, clear all storage first
                if (isAgentAccess) {
                    // Clear all auth storage to prevent conflicts
                    clearAuthStorage()

                    // Set isAgent flag in the loginInformation
                    const agentLoginInfo = {
                        ...loginInformation,
                        idAsesor: loginInformation.idAsesor || 1 // Ensure isAgent flag is set
                    }

                    // Use regular setAuth with modified data
                    setAuth(agentLoginInfo)

                    // Store tokens in sessionStorage
                    setItem('accessToken', loginInformation.token.access_token)
                    setItem(
                        'refreshToken',
                        loginInformation.token.refresh_token
                    )
                } else {
                    // For regular aspirant login, first ensure no agent data exists
                    if (sessionStorage.length > 0) {
                        // If there are sessionStorage items, clear them to avoid conflicts
                        clearAuthStorage()
                    }
                    // Regular aspirant login
                    setAuth(loginInformation)
                }

                try {
                    localStorage.removeItem('suppressErrorToasts')
                } catch {}
                toast.success('Successfully logged in')
                try {
                    const redirect = localStorage.getItem('postLoginRedirect')
                    const target =
                        redirect &&
                        typeof redirect === 'string' &&
                        redirect.length > 0
                            ? redirect
                            : '/initial-inscription'
                    if (redirect) {
                        localStorage.removeItem('postLoginRedirect')
                    }
                    navigate(target, { replace: true })
                } catch {
                    navigate('/initial-inscription', { replace: true })
                }
            } catch (error) {
                console.error('Login failed:', error)
                toast.error('Login failed. Please try again.')
                throw error
            } finally {
                setIsLoading(false)
            }
        },
        [navigate, setIsLoading, setAuth, clearAuthStorage, setItem]
    )

    const logout = useCallback(() => {
        console.log('Logging out user...')
        try {
            const href =
                window.location.pathname +
                window.location.search +
                window.location.hash
            if (window.location.pathname !== '/login') {
                localStorage.setItem('postLoginRedirect', href)
            }
            localStorage.setItem('suppressErrorToasts', 'true')
        } catch {}

        // Explicitly clear token from both storage types to be extra safe
        try {
            // Clear tokens from localStorage
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')

            // Clear tokens from sessionStorage (for agents)
            sessionStorage.removeItem('accessToken')
            sessionStorage.removeItem('refreshToken')
            sessionStorage.removeItem('agent-accessToken')
            sessionStorage.removeItem('agent-refreshToken')
        } catch (e) {
            console.error('Error clearing tokens directly:', e)
        }

        // Use the storage utility to clear all auth-related items
        clearAuthStorage()

        // Clear store state
        clearAuth()

        // Force navigation to login page
        navigate('/login')
    }, [clearAuth, navigate, clearAuthStorage])

    const getAccessToken = useCallback(() => {
        // Check sessionStorage first (for agents), then localStorage
        if (isAgent) {
            return sessionStorage.getItem('accessToken') || accessToken
        }
        return accessToken ?? null
    }, [accessToken, isAgent])

    const getRefreshToken = useCallback(() => {
        // Check sessionStorage first (for agents), then localStorage
        if (isAgent) {
            return sessionStorage.getItem('refreshToken') || refreshToken
        }
        return refreshToken ?? null
    }, [refreshToken, isAgent])

    const getNewAccessToken = useCallback(
        async (
            oldAccessToken: string,
            refreshToken: string
        ): Promise<string | null> => {
            try {
                if (!oldAccessToken || !refreshToken) {
                    console.error('Missing tokens for refresh')
                    logout()
                    return null
                }

                // Create a new axios instance for this specific request
                const tokenAxios = axios.create({
                    baseURL: ApiRoutes.AUTH,
                    headers: { 'Content-Type': 'application/json' },
                    timeout: 10000 // 10 second timeout for token refresh
                })

                const response = await tokenAxios.post(
                    '/sessions/refresh-token',
                    {
                        oldAccessToken,
                        refreshToken
                    }
                )

                console.log('Token refresh response:', response.data)

                // Extract tokens from response, with fallbacks for different response structures
                const newAccessToken = response.data?.data?.access_token
                const newRefreshToken = response.data?.data?.refresh_token

                if (!newAccessToken || !newRefreshToken) {
                    console.error(
                        'Invalid token data in response',
                        response.data
                    )
                    logout()
                    return null
                }

                console.log('New tokens:', {
                    accessToken: newAccessToken.substring(0, 20) + '...',
                    refreshToken: newRefreshToken.substring(0, 10) + '...'
                })

                // First update the store to ensure state consistency
                updateTokens({
                    accessToken: newAccessToken,
                    refreshToken: newRefreshToken
                })

                // Then store tokens directly in the appropriate storage
                if (isAgent) {
                    // For agents, update sessionStorage
                    try {
                        sessionStorage.setItem('accessToken', newAccessToken)
                        sessionStorage.setItem('refreshToken', newRefreshToken)
                    } catch (err) {
                        console.error('Failed to update sessionStorage:', err)
                        logout()
                        return null
                    }
                } else {
                    // For regular users, update localStorage
                    try {
                        localStorage.setItem('accessToken', newAccessToken)
                        localStorage.setItem('refreshToken', newRefreshToken)
                    } catch (err) {
                        console.error('Failed to update localStorage:', err)
                        logout()
                        return null
                    }
                }

                // Critical: Add delay to ensure store updates are propagated
                await new Promise((resolve) => setTimeout(resolve, 200))

                // Double-check storage was updated correctly
                const storedToken = isAgent
                    ? sessionStorage.getItem('accessToken')
                    : localStorage.getItem('accessToken')

                if (!storedToken) {
                    console.error('Token storage failed! Storage has no token')
                    logout()
                    return null
                }

                return newAccessToken
            } catch (error) {
                console.error('Failed to refresh token:', error)
                // Force logout on critical token refresh errors
                logout()
                return null
            }
        },
        [updateTokens, isAgent, logout]
    )

    // Mark context as initialized when mounted
    useEffect(() => {
        setIsInitialized(true)

        return () => setIsInitialized(false)
    }, [])

    return (
        <AuthContext.Provider
            value={{
                user,
                idAspirant,
                accessToken,
                refreshToken,
                login,
                logout,
                isAuthenticated,
                isAgent,
                idAsesor,
                firstName,
                lastName,
                email,
                cellphone,
                studyModality,
                getAccessToken,
                getRefreshToken,
                getNewAccessToken,
                idInscription,
                programName
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext)

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }

    return context
}

// Protected Route Component
export function RequireAuth({ children }: { children: React.ReactNode }) {
    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login')
        }
    }, [isAuthenticated, navigate])

    return isAuthenticated ? <>{children}</> : null
}
